import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, ChevronDown } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'

function Navbar({ transparent = false }) {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [companyOpen, setCompanyOpen] = useState(false)
  const [solutionsOpen, setSolutionsOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const companyRef = useRef(null)
  const solutionsRef = useRef(null)
  const userMenuRef = useRef(null)
  const { user, isAuthenticated, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (companyRef.current && !companyRef.current.contains(e.target)) {
        setCompanyOpen(false)
      }
      if (solutionsRef.current && !solutionsRef.current.contains(e.target)) {
        setSolutionsOpen(false)
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    setCompanyOpen(false)
    setSolutionsOpen(false)
  }, [location.pathname, location.hash])

  const isHome = location.pathname === '/'
  const navLinks = [
    { name: 'Home', href: '/', isRoute: true },
    { name: 'Security', href: '/#security', isRoute: true },
    { name: 'Pricing', href: '/pricing', isRoute: true },
  ]

  const showBackground = isScrolled || !transparent

  const solutionsLinks = [
    { name: 'For External Engagements', to: '/external-engagements' },
    { name: 'For Founders', to: '/founders' },
    { name: 'For Leadership Teams', to: '/leadership' },
    { name: 'For Engineering Teams', to: '/engineering' },
    { name: 'For Sales Teams', to: '/sales' },
    { name: 'For Operations Teams', to: '/operations' },
  ]

  const companyLinks = [
    { name: 'About Us', to: '/about' },
    { name: 'FAQs', to: '/faqs' },
  ]

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          showBackground
            ? 'bg-white/80 backdrop-blur-xl border-b border-gray-200 shadow-sm'
            : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <div className="w-12 h-10 flex items-center justify-center">
                <img src="/rithmo-mark.png" alt="Rithmo logo" className="w-full h-full object-contain" />
              </div>
              <span className="text-xl font-bold text-gray-900">Rithmo</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="text-gray-600 hover:text-gray-900 transition-colors duration-200 underline-effect"
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    className="text-gray-600 hover:text-gray-900 transition-colors duration-200 underline-effect"
                  >
                    {link.name}
                  </a>
                )
              ))}
              <div className="relative" ref={solutionsRef}>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors duration-200 underline-effect"
                  aria-expanded={solutionsOpen}
                  aria-haspopup="true"
                  aria-controls="solutions-nav-menu"
                  onClick={() => setSolutionsOpen((o) => !o)}
                >
                  Solutions
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${solutionsOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </button>
                {solutionsOpen && (
                  <div
                    id="solutions-nav-menu"
                    role="menu"
                    aria-orientation="vertical"
                    className="absolute right-0 top-full mt-2 py-2 w-max rounded-xl bg-white border border-gray-200 shadow-lg z-50"
                  >
                    {solutionsLinks.map((item) => (
                      <Link
                        key={item.to}
                        role="menuitem"
                        to={item.to}
                        className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors whitespace-nowrap"
                        onClick={() => setSolutionsOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative" ref={companyRef}>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors duration-200 underline-effect"
                  aria-expanded={companyOpen}
                  aria-haspopup="true"
                  aria-controls="company-nav-menu"
                  onClick={() => setCompanyOpen((o) => !o)}
                >
                  Company
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${companyOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </button>
                {companyOpen && (
                  <div
                    id="company-nav-menu"
                    role="menu"
                    aria-orientation="vertical"
                    className="absolute right-0 top-full mt-2 py-2 min-w-[12rem] rounded-xl bg-white border border-gray-200 shadow-lg z-50"
                  >
                    {companyLinks.map((item) => (
                      <Link
                        key={item.to}
                        role="menuitem"
                        to={item.to}
                        className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                        onClick={() => setCompanyOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-4">
              {isAuthenticated && user ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    type="button"
                    onClick={() => setUserMenuOpen((o) => !o)}
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 text-white font-semibold text-sm flex items-center justify-center hover:opacity-90 transition-opacity"
                  >
                    {(user.full_name || user.email || '')
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase() || 'U'}
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 py-2 min-w-[10rem] rounded-xl bg-white border border-gray-200 shadow-lg z-50">
                      <Link
                        to="/dashboard"
                        className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Dashboard
                      </Link>
                      <button
                        type="button"
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                        onClick={async () => {
                          setUserMenuOpen(false)
                          await logout()
                          navigate('/')
                        }}
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link to="/login" className="btn-ghost">
                    Sign In
                  </Link>
                  <Link to="/signup" className="btn-primary">
                    Try Rithmo
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-gray-600 hover:text-gray-900"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 pt-20 bg-white/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col items-center gap-6 p-8">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="text-xl text-gray-600 hover:text-gray-900 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    className="text-xl text-gray-600 hover:text-gray-900 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </a>
                )
              ))}
              <div className="w-full max-w-xs flex flex-col gap-3 pt-4 border-t border-gray-200">
                <span className="text-sm font-semibold text-gray-400 text-center">Solutions</span>
                {solutionsLinks.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="text-xl text-gray-600 hover:text-gray-900 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
              <div className="w-full max-w-xs flex flex-col gap-3 pt-4 border-t border-gray-200">
                <span className="text-sm font-semibold text-gray-400 text-center">Company</span>
                {companyLinks.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="text-xl text-gray-600 hover:text-gray-900 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
              <div className="flex flex-col gap-4 w-full mt-8">
                {isAuthenticated && user ? (
                  <>
                    <Link
                      to="/dashboard"
                      className="btn-primary text-center"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <button
                      type="button"
                      className="btn-secondary text-center"
                      onClick={async () => {
                        setIsMobileMenuOpen(false)
                        await logout()
                        navigate('/')
                      }}
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="btn-secondary text-center"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/signup"
                      className="btn-primary text-center"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Try Rithmo
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default Navbar
